/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { FocusZone } from '@fluentui/react/lib/FocusZone';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { routeTable } from 'src/core/utils/routeTable';
import { LuisAppHeaderItems } from 'src/luis/appShell/appPage/components/LuisAppHeaderItems';
import { DropdownBreadcrumbItem } from 'src/modules/subHeader/components/DropdownBreadcrumbItem';
import { HeaderBreadcrumbItemProps } from 'src/modules/subHeader/components/HeaderBreadcrumbItem';
import { onRenderBreadcrumbs, onRenderOverflowBreadcrumbs, SubHeaderRoot } from 'src/modules/subHeader/utils/subHeaderUtils';

const messages = defineMessages({
    myLuis: {
        id: 'luis.luisAppPageSubHeader.myLuis',
        defaultMessage: 'My LUIS'
    },
    moreOptions: {
        id: 'luis.luisAppPageSubHeader.moreOptions',
        defaultMessage: 'More options'
    }
});

export const LuisAppPageSubHeader = observer(() => {
    const history = useHistory();
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { isLargeOrGreater } = useResponsiveMode();

    const currentApp = rootStore.currentLuisAppStore;

    const items: HeaderBreadcrumbItemProps[] = [
        {
            text: localize(messages.myLuis),
            onClick: () => history.push(routeTable.luis.root())
        },
        {
            isSelected: true,
            text: currentApp?.name,
            secondaryText: `V${currentApp?.activeVersion}`,
            onRender: item => (currentApp ? <DropdownBreadcrumbItem item={item} dropdownMode="luisAppDropdown" /> : null)
        }
    ];

    return (
        <FocusZone id="luisAppPageSubHeader" role="navigation">
            <SubHeaderRoot horizontal verticalAlign="center">
                {isLargeOrGreater && onRenderBreadcrumbs(items)}
                {!isLargeOrGreater && onRenderOverflowBreadcrumbs(items, localize(messages.moreOptions))}
                <LuisAppHeaderItems />
            </SubHeaderRoot>
        </FocusZone>
    );
});
